// JourneyComplete — the closing ceremony for the WHOLE activity. Fires once
// when the learner finishes the final audio segment「再見。再見」(the
// namesake track). A solemn full-stop, not a transition.
//
// Mirrors PhotoRevealCeremony's choreographic language (ink900 overlay,
// RailLayer + FogLayer atmosphere, phase-timed fade-in, reduced-motion
// static fallback, soft vibrate) but the content is two text lines, slow and
// large. The wording comes from the final segment's own narration, which
// reveals the activity's double meaning: 「再見」= farewell to the past self,
// and 「再見」= seeing your true self again. Three 「再見」total (two line
// endings + the button) close the loop on the event's name 再見。再見.
//
// Cadence is deliberately slower than PhotoReveal (this is the ending): line 1
// at 600ms, line 2 at 2600ms, button at 4600ms. Text is large (size 24/22) and
// minimal so it reads clearly.

import { FogLayer, Han, RailLayer, T, TextBtn, useReducedMotion } from "@goodbye/ui";
import { useEffect, useRef, useState } from "react";

export interface JourneyCompleteProps {
  onDone: () => void;
}

export function JourneyComplete({ onDone }: JourneyCompleteProps) {
  const reduced = useReducedMotion();
  // phase 0 black hold → 1 line1 → 2 line2 → 3 button. reduced jumps to 3.
  const [phase, setPhase] = useState(reduced ? 3 : 0);
  const vibratedRef = useRef(false);

  useEffect(() => {
    if (reduced) return;
    const timers = [
      window.setTimeout(() => setPhase(1), 600),
      window.setTimeout(() => setPhase(2), 2600),
      window.setTimeout(() => setPhase(3), 4600),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [reduced]);

  // One soft farewell pulse as line 1 lands — gentler than PhotoReveal's
  // celebratory pattern; this is a quiet goodbye.
  useEffect(() => {
    if (reduced || vibratedRef.current) return;
    if (phase >= 1 && typeof navigator !== "undefined" && navigator.vibrate) {
      vibratedRef.current = true;
      const t = window.setTimeout(() => {
        try {
          navigator.vibrate?.(80);
        } catch {
          /* iOS / unsupported — ignore */
        }
      }, 120);
      return () => window.clearTimeout(t);
    }
  }, [phase, reduced]);

  // A line: main text + a trailing「再見」that's slightly larger / its own
  // beat. Fades + rises in once `show` is true. `mainLs` lets a longer line
  // use tighter tracking so it stays on ONE line (no orphaned 字 wrapping).
  const line = (
    main: string,
    farewell: string,
    show: boolean,
    mainLs = "0.18em",
  ): React.ReactNode => (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(8px)",
        transition: reduced
          ? undefined
          : "opacity 1200ms var(--ease-mist), transform 1200ms var(--ease-mist)",
      }}
    >
      <Han
        size={22}
        color={T.paper}
        lh={1.9}
        ls={mainLs}
        style={{ textAlign: "center", whiteSpace: "nowrap" }}
      >
        {main}
      </Han>
      <Han size={26} color={T.paper} ls="0.4em" style={{ textAlign: "center" }}>
        {farewell}
      </Han>
    </div>
  );

  return (
    <div
      aria-label="旅程結束"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: T.ink900,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 56,
        padding: "0 32px",
      }}
    >
      {!reduced && (
        <>
          <RailLayer />
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: phase >= 1 ? 0.4 : 0,
              transition: "opacity 1400ms var(--ease-mist)",
            }}
          >
            <FogLayer opacity={1} />
          </div>
        </>
      )}

      {line("向 過 去 的 自 己", "再 見", reduced || phase >= 1)}
      {/* Longer line → tighter tracking so 「己」doesn't wrap to a 2nd line
          on narrow phones (keeps it on one line via nowrap + 0.06em). */}
      {line("重 新 真 正 地 再 次 見 到 自 己", "再 見", reduced || phase >= 2, "0.06em")}

      {(reduced || phase >= 3) && (
        <TextBtn
          onClick={onDone}
          mono={false}
          size={16}
          ls="0.4em"
          color={T.paper}
          framed={false}
          style={{
            position: "relative",
            zIndex: 1,
            // Visible outline so it reads as a button on the dark overlay
            // (framed's press-bg is invisible on ink-900).
            border: `1px solid ${T.paper}`,
            padding: "12px 28px",
            opacity: 1,
            transition: reduced ? undefined : "opacity 1000ms var(--ease-mist)",
          }}
        >
          前 往 下 一 站
        </TextBtn>
      )}
    </div>
  );
}
